/**
 * Stick and trigger conditioning. Sticks get a radial deadzone with rescale so
 * the first usable value is small rather than a jump to the deadzone edge.
 * Triggers get a small deadzone and, for Reach, the reach curve. Buttons are
 * never filtered.
 */

export interface Vec2 {
  x: number;
  y: number;
}

/** Radial deadzone with rescale, written into `out` (no allocation). */
export function radialDeadzone(x: number, y: number, deadzone: number, out: Vec2): void {
  const m = Math.sqrt(x * x + y * y);
  if (m <= deadzone || m === 0) {
    out.x = 0;
    out.y = 0;
    return;
  }
  const scaled = Math.min(1, (m - deadzone) / (1 - deadzone));
  out.x = (x / m) * scaled;
  out.y = (y / m) * scaled;
}

export function triggerDeadzone(v: number, deadzone: number): number {
  if (v <= deadzone) return 0;
  return Math.min(1, (v - deadzone) / (1 - deadzone));
}

/**
 * Reach curve: `1 - (1 - p)^k`. With k = 3.15 a 40% press gives an 80%
 * extension (the Standard scheme); k = 1 is linear (the Ninja scheme).
 */
export function reachCurve(p: number, k: number): number {
  if (p <= 0) return 0;
  if (p >= 1) return 1;
  if (k === 1) return p;
  return 1 - (1 - p) ** k;
}
